// src/pages/LoginPage.jsx
import React, { useState, useEffect } from "react";
import {
  User,
  Lock,
  Shield,
  Zap,
  ArrowRight,
  CheckCircle,
  X,
  AlertCircle,
  Home,
  UserPlus,
  KeyRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import Logo from "../assets/logo.png";

export default function LoginPage() {
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const nav = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      await login(id, pw);
      setShowToast(true);
      setTimeout(() => nav("/app"), 500);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Toast 컴포넌트
  const Toast = ({ open, text, onDone }) => {
    useEffect(() => {
      if (open) {
        const timer = setTimeout(onDone, 3000);
        return () => clearTimeout(timer);
      }
    }, [open, onDone]);
    if (!open) return null;
    return (
      <div className="fixed top-6 right-6 z-50 animate-slide-in">
        <div className="bg-gray-900/95 backdrop-blur-xl border border-gray-800 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-2xl">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <p className="text-white font-medium">{text}</p>
        </div>
      </div>
    );
  };

  // Error Modal 컴포넌트
  const ErrorModal = ({ open, message, onClose }) => {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative bg-gray-900/95 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 max-w-sm w-full animate-modal-appear">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">로그인 실패</h3>
              <p className="text-gray-400 text-sm">{message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[150px] opacity-30 animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-[150px] opacity-30 animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-600 rounded-full filter blur-[200px] opacity-20 animate-pulse delay-1000"></div>
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
            }}
          ></div>
        </div>
      </div>

      <div className="relative z-10 min-h-screen flex">
        {/* 좌측 상단에 대시보드로 돌아가기 버튼 */}
        <button
          onClick={() => nav("/")}
          className="absolute top-6 left-6 flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
        >
          <Home className="w-5 h-5" />
          <span className="font-medium">돌아가기</span>
        </button>

        <div className="w-full max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 px-6 py-12">
          {/* Left: Hero Section */}
          <div className="flex flex-col justify-center">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-6">
                <img
                  src={Logo}
                  alt="elva logo"
                  className="w-20 h-20 text-white"
                />
              </div>
              <h1 className="text-6xl lg:text-7xl font-bold mb-6">
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Elva
                </span>
              </h1>
              <p className="text-2xl lg:text-3xl text-gray-300 leading-relaxed mb-4">
                외부유출 없는{" "}
                <span className="font-semibold text-white">기업용 AI</span>{" "}
                서비스
              </p>
              <p className="text-lg text-gray-500 leading-relaxed max-w-lg">
                차세대 MoE 아키텍처 기반으로 기업이 안전하게 사용할 수 있는 사무
                자동화 AI Agent를 제공합니다.
              </p>
            </div>
            {/* Features */}
            <div className="grid grid-cols-2 gap-4 mt-12">
              <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-2xl p-4">
                <Shield className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="text-white font-semibold mb-1">
                  엔터프라이즈 보안
                </h3>
                <p className="text-gray-500 text-sm">
                  데이터 유출 걱정 없는 안전한 AI
                </p>
              </div>
              <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-2xl p-4">
                <Zap className="w-8 h-8 text-purple-400 mb-3" />
                <h3 className="text-white font-semibold mb-1">맞춤형 솔루션</h3>
                <p className="text-gray-500 text-sm">
                  필요한 기능만 선택하여 비용 절감
                </p>
              </div>
            </div>
          </div>

          {/* Right: Login Form */}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-md">
              <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-2">로그인</h2>
                <p className="text-gray-400 mb-8">Elva 대시보드에 접속하세요</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      아이디
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="text"
                        required
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="아이디를 입력하세요"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-300">
                        비밀번호
                      </label>
                      <button
                        type="button"
                        onClick={() => nav("/sig_reset_forget_xpow")}
                        className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        비밀번호를 잊으셨나요?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        required
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="비밀번호를 입력하세요"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full group bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        로그인 중...
                      </>
                    ) : (
                      <>
                        로그인
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>

                {/* 구분선 */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-800"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-gray-900/50 px-4 text-gray-500">
                      또는
                    </span>
                  </div>
                </div>

                {/* 회원가입 버튼 */}
                <button
                  onClick={() => nav("/sig_pagi_signup")}
                  className="w-full group bg-gray-800/50 hover:bg-gray-800 border border-gray-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  회원가입
                  <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </button>

                {/* 추가 링크들 */}
                <div className="mt-6 flex flex-col gap-3">
                  <div className="flex items-center justify-center gap-6 text-sm">
                    <button
                      onClick={() => nav("/sig_reset_forget_xpow")}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                    >
                      <KeyRound className="w-4 h-4" />
                      비밀번호 찾기
                    </button>
                    <span className="text-gray-700">|</span>
                    <button
                      onClick={() => nav("/sig_pagi_signup")}
                      className="text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                    >
                      <UserPlus className="w-4 h-4" />
                      계정 만들기
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      <Toast
        open={showToast}
        text="로그인에 성공하였습니다."
        onDone={() => setShowToast(false)}
      />

      {/* Error Modal */}
      <ErrorModal open={!!error} message={error} onClose={() => setError("")} />

      <style jsx>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }

        @keyframes modal-appear {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-modal-appear {
          animation: modal-appear 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
